import React, { useState } from 'react';
import { useAuth } from '../../src/contexts/AuthContext';
import AdminSchedule from './AdminSchedule';
import AdminInventory from './AdminInventory';
import AdminAnalytics from './AdminAnalytics';
import VetRecords from './VetRecords';
import AdminServices from './AdminServices';
import AdminVets from './AdminVets';
import AdminOrders from './AdminOrders';
import { api } from '../../src/services/api-new';

interface Props {
    onNavigate: (view: any) => void;
    onBack: () => void;
}

const AdminDashboard: React.FC<Props> = ({ onNavigate, onBack }) => {
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState<'overview' | 'schedule' | 'inventory' | 'records' | 'services' | 'vets' | 'orders'>('overview');
    const [selectedPatientId, setSelectedPatientId] = useState<string | undefined>(undefined);
    const [activeAppointmentId, setActiveAppointmentId] = useState<number | undefined>(undefined);

    const handleStartConsultation = async (patientId: string, appointmentId: number) => {
        try {
            await api.appointments.updateStatus(appointmentId, 'ANAMNESA');
            setSelectedPatientId(patientId);
            setActiveAppointmentId(appointmentId);
            setActiveTab('records');
            console.log('Admin starting consultation for patient:', patientId);
        } catch (error) {
            console.error("Failed to start consultation:", error);
            alert("Failed to start consultation");
        }
    };

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 scroll-smooth">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Dashboard Header */}
                <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                        >
                            <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                        </button>
                        <div>
                            <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                                <span>Clinic Command</span>
                                <span className="material-icons-round text-sm">chevron_right</span>
                                <span className="text-primary">{activeTab}</span>
                            </nav>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight flex items-center gap-3">
                                Welcome, Dr. {user?.name?.split(' ')[0] || 'Admin'} <span className="animate-bounce-slow">👨‍⚕️</span>
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 font-bold mt-1 text-sm uppercase tracking-wider">
                                System Status: <span className="text-green-500">Operational</span> • {new Date().toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={logout}
                            className="bg-white dark:bg-surface-dark text-red-500 border-2 border-red-100 hover:bg-red-50 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-icons-round text-lg">logout</span> Logout
                        </button>
                    </div>
                </header>

                {/* Tabs */}
                <div className="flex bg-white dark:bg-surface-dark/50 backdrop-blur-md p-1.5 rounded-[1.5rem] shadow-sm border border-gray-100 dark:border-gray-800 w-fit overflow-x-auto no-scrollbar max-w-full">
                    <Tab label="Analytics" icon="analytics" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                    <Tab label="Schedule" icon="calendar_month" active={activeTab === 'schedule'} onClick={() => setActiveTab('schedule')} />
                    <Tab label="Inventory" icon="inventory_2" active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} />
                    <Tab label="Records" icon="folder_shared" active={activeTab === 'records'} onClick={() => setActiveTab('records')} />
                    <Tab label="Services" icon="medical_services" active={activeTab === 'services'} onClick={() => setActiveTab('services')} />
                    <Tab label="Vets" icon="person" active={activeTab === 'vets'} onClick={() => setActiveTab('vets')} />
                    <Tab label="Orders" icon="shopping_bag" active={activeTab === 'orders'} onClick={() => setActiveTab('orders')} />
                </div>

                {/* Content Area */}
                <div className="animate-fade-in-up">
                    {activeTab === 'overview' && <AdminAnalytics />}
                    {activeTab === 'schedule' && <AdminSchedule onStartConsultation={handleStartConsultation} />}
                    {activeTab === 'inventory' && <AdminInventory />}
                    {activeTab === 'records' && <VetRecords initialPatientId={selectedPatientId} appointmentId={activeAppointmentId} />}
                    {activeTab === 'services' && <AdminServices />}
                    {activeTab === 'vets' && <AdminVets />}
                    {activeTab === 'orders' && <AdminOrders />}
                </div>
            </div>
        </div>
    );
};

const Tab = ({ label, icon, active, onClick }: any) => (
    <button onClick={onClick} className={`flex items-center gap-2 px-6 py-3 rounded-2xl transition-all whitespace-nowrap font-black text-xs uppercase tracking-widest ${active ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}>
        <span className="material-icons-round text-lg">{icon}</span>
        {label}
    </button>
);

export default AdminDashboard;
